import { useState } from "react";

import { AlertTriangle, Calendar, Pencil, Sparkles, X } from "lucide-react";

import { DatePicker } from "@/components/cinch/DatePicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCan } from "@/lib/auth/useCan";
import { cn } from "@/lib/utils";

import {
  useSuggestTermStart,
  useUpsertAnnotation,
  type FirstTermStartSource,
  type PipelineAnnotation,
  type TermStartSuggestion,
} from "../../api";
import { fmtDate } from "../../format";

/**
 * "Renewal anchor" editor — pins an account's renewal cycle to a known first
 * term start (always first-of-month). Three quick affordances cover the
 * common picks (First billed, Signed, Custom via DatePicker); the chosen
 * source is recorded so the filled state explains *why* this date.
 *
 * Empty until the CSM picks something. Once set, the field drives the
 * renewal-month derivation in pipeline_service.
 */
export function FirstTermStartEditor({
  qbId,
  annotation,
  firstBilledMonth,
  contractSignedAt,
  disabled,
}: {
  qbId: number;
  annotation: PipelineAnnotation;
  /** First month the account was billed. Used to seed the "First billed" button. */
  firstBilledMonth: string | null;
  /** Master contract signed date. Used to seed the "Signed" button. */
  contractSignedAt: string | null;
  disabled?: boolean;
}) {
  const upsert = useUpsertAnnotation();
  const suggest = useSuggestTermStart();
  const canAdmin = useCan("renewals.admin");
  // `mode` controls the editing surface. `view` is the resting state when a
  // value is already set; `pick` shows the three buttons + custom picker;
  // `custom` opens the DatePicker inline.
  type Mode = "view" | "pick" | "custom";
  const initial: Mode = annotation.first_term_start_date ? "view" : "pick";
  const [mode, setMode] = useState<Mode>(initial);
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const [suggestion, setSuggestion] = useState<TermStartSuggestion | null>(null);

  const value = annotation.first_term_start_date;
  const source = annotation.first_term_start_source;

  // Both quick-pick inputs may be null (account hasn't been billed yet, or
  // contract isn't synced) — render the button disabled in that case so the
  // user can still pick custom.
  const firstBilledDate = monthToFirst(firstBilledMonth);
  const signedDate = monthToFirst(contractSignedAt);

  function commit(next: string | null, src: FirstTermStartSource | null) {
    upsert.mutate(
      {
        qbId,
        payload: { first_term_start_date: next, first_term_start_source: src },
      },
      {
        onSuccess: () => setMode(next ? "view" : "pick"),
      },
    );
  }

  // ───── filled state ─────
  if (mode === "view" && value) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">First term started {fmtDate(value)}</span>
            {source && <Badge variant="neutral">{sourceLabel(source)}</Badge>}
          </div>
          <div className="text-xs text-muted-foreground">
            Renewal cycle walks forward from this anchor.
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMode("pick")}
            disabled={disabled}
            title="Change the anchor"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {canAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => commit(null, null)}
              disabled={disabled || upsert.isPending}
              title="Clear the anchor (admin only)"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ───── custom-pick state ─────
  if (mode === "custom") {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/30 p-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          Pick custom date
        </div>
        <DatePicker
          value={customDate}
          onChange={setCustomDate}
          aria-label="Term start"
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            disabled={!customDate || disabled || upsert.isPending}
            onClick={() =>
              customDate && commit(toIsoFirstOfMonth(customDate), "custom")
            }
          >
            {upsert.isPending ? "Saving…" : "Use this date"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMode(value ? "view" : "pick")}
            disabled={upsert.isPending}
          >
            Cancel
          </Button>
          <span className="ml-auto text-[11px] text-muted-foreground">
            Auto-snaps to first of month.
          </span>
        </div>
      </div>
    );
  }

  // ───── empty / picking state ─────
  return (
    <div className="flex flex-col gap-2 rounded-md border border-dashed border-border bg-muted/20 p-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">No first term anchor set</span>
          <span className="text-xs text-muted-foreground">
            Set this to drive the renewal anniversary deterministically.
          </span>
        </div>
        {value && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMode("view")}
            disabled={upsert.isPending}
          >
            Cancel
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <QuickButton
          label="First billed"
          date={firstBilledDate}
          disabled={!firstBilledDate || disabled || upsert.isPending}
          onClick={() =>
            firstBilledDate && commit(firstBilledDate, "first_billed")
          }
        />
        <QuickButton
          label="Signed"
          date={signedDate}
          disabled={!signedDate || disabled || upsert.isPending}
          onClick={() => signedDate && commit(signedDate, "signed")}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMode("custom")}
          disabled={disabled || upsert.isPending}
        >
          Custom…
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            suggest.mutate(qbId, {
              onSuccess: (data) => setSuggestion(data),
            })
          }
          disabled={disabled || suggest.isPending || upsert.isPending}
          title="Have an AI sanity-check the first full-rate billing month"
          className="gap-1"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {suggest.isPending ? "Thinking…" : "Suggest"}
        </Button>
      </div>
      {suggestion && (
        <SuggestionCard
          suggestion={suggestion}
          onUse={() =>
            suggestion.suggested_date &&
            commit(suggestion.suggested_date, "ai_suggested")
          }
          onDismiss={() => setSuggestion(null)}
          disabled={disabled || upsert.isPending}
        />
      )}
    </div>
  );
}

function SuggestionCard({
  suggestion,
  onUse,
  onDismiss,
  disabled,
}: {
  suggestion: TermStartSuggestion;
  onUse: () => void;
  onDismiss: () => void;
  disabled?: boolean;
}) {
  const hasDate = !!suggestion.suggested_date;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-info/30 bg-info-light/40 p-3 text-sm">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 text-info" />
        <div className="flex flex-col gap-0.5">
          {hasDate ? (
            <>
              <div className="font-semibold">
                Suggested: {fmtDate(suggestion.suggested_date)}
              </div>
              <div className="text-xs text-muted-foreground">
                {suggestion.deterministic_reason}
              </div>
            </>
          ) : (
            <div className="font-medium text-muted-foreground">
              {suggestion.deterministic_reason}
            </div>
          )}
        </div>
      </div>
      {suggestion.ai_assessment === "warning" && suggestion.ai_note && (
        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-light px-2 py-1.5 text-xs text-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <b>Heads up:</b> {suggestion.ai_note}
          </span>
        </div>
      )}
      {suggestion.ai_error && (
        <div className="text-[11px] italic text-muted-foreground">
          AI assessment unavailable ({suggestion.ai_error}). Deterministic
          suggestion above is still valid.
        </div>
      )}
      <div className="flex items-center gap-2">
        {hasDate && (
          <Button size="sm" onClick={onUse} disabled={disabled}>
            Use this date
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onDismiss} disabled={disabled}>
          Dismiss
        </Button>
        {suggestion.ai_model && (
          <span className="ml-auto text-[10px] text-muted-foreground">
            via {suggestion.ai_model}
          </span>
        )}
      </div>
    </div>
  );
}

function QuickButton({
  label,
  date,
  disabled,
  onClick,
}: {
  label: string;
  /** ISO YYYY-MM-DD on first-of-month, or null when source data is missing. */
  date: string | null;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className={cn("flex flex-col items-start gap-0 h-auto py-1.5 px-3")}
    >
      <span className="text-sm font-medium">{label}</span>
      <span className="text-[11px] font-normal text-muted-foreground">
        {date ? fmtDate(date) : "—"}
      </span>
    </Button>
  );
}

function sourceLabel(s: FirstTermStartSource): string {
  switch (s) {
    case "first_billed":
      return "from first billed";
    case "signed":
      return "from signed date";
    case "custom":
      return "custom";
    case "ai_suggested":
      return "from AI suggestion";
  }
}

/** YYYY-MM or YYYY-MM-DD → first-of-month ISO date string. */
function monthToFirst(s: string | null): string | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]}-01`;
}

/** JS Date in local tz → first-of-month ISO date string. */
function toIsoFirstOfMonth(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}
